import { useState, useEffect, useCallback, useRef } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Button } from "@relume_io/relume-ui";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link } from "react-router-dom";

const SLIDE_DURATION = 5000; // 5 segundos

interface Slide {
  id: number;
  title: string;
  description: string;
  image: string;
  link: string;
  buttonText: string;
}

const slides: Slide[] = [
  {
    id: 1,
    title: "Performance construída na estrada e na força.",
    description: "Metodologia de treinamento funcional e preparação física para quem busca consistência, saúde e evolução real no esporte.",
    image: "https://res.cloudinary.com/tatikka/image/upload/v1778712518/hero-1_rnaiix.jpg",
    link: "/treinos",
    buttonText: "Conhecer Metodologia"
  },
  {
    id: 2,
    title: "Treinamento de elite para o seu pedal.",
    description: "Planilhas individuais e acompanhamento direto com Dudu Daneluci, atleta de elite e treinador de mais de 180 atletas pelo país.",
    image: "https://res.cloudinary.com/tatikka/image/upload/v1778712518/hero-2_r1dbem.jpg",
    link: "/assessoria-ciclismo",
    buttonText: "Conhecer Assessoria Online"
  },
  {
    id: 3,
    title: "Imersão nas montanhas da Canastra.",
    description: "Dias intensos de altimetria, técnica de pilotagem e convivência de atleta profissional nas trilhas mais desafiadoras de Minas Gerais.",
    image: "https://res.cloudinary.com/tatikka/image/upload/v1778712518/hero-3_f8adn8.jpg",
    link: "/training-camps",
    buttonText: "Ver Próximos Camps"
  },
];

export function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);



  const startTimer = useCallback(() => {
    if (!hasInteracted) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, SLIDE_DURATION);
  }, [hasInteracted]);

  useEffect(() => {
    const handleInteraction = () => {
      setHasInteracted(true);
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("mouseenter", handleInteraction);
    };

    window.addEventListener("scroll", handleInteraction, { passive: true });
    window.addEventListener("click", handleInteraction);
    window.addEventListener("touchstart", handleInteraction, { passive: true });
    window.addEventListener("mouseenter", handleInteraction, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("mouseenter", handleInteraction);
    };
  }, []);

  useEffect(() => {
    if (hasInteracted) {
      startTimer();
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (timerRef.current) clearInterval(timerRef.current);
      } else if (hasInteracted) {
        startTimer();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!hasInteracted) setHasInteracted(true);
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [startTimer, hasInteracted]);

  const nextSlide = () => {
    if (!hasInteracted) setHasInteracted(true);
    setCurrent((prev) => (prev + 1) % slides.length);
    startTimer();
  };

  const prevSlide = () => {
    if (!hasInteracted) setHasInteracted(true);
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
    startTimer();
  };

  const goToSlide = (index: number) => {
    if (!hasInteracted) setHasInteracted(true);
    setCurrent(index);
    startTimer();
  };

  const getImageUrl = (url: string, _width: number) => {
    if (url.startsWith("/images/")) return url;
    return url.replace("/upload/", `/upload/f_avif,w_${_width}/`);
  };

  return (
    <section 
      className="px-[5%] py-8" 
      aria-roledescription="carousel" 
      aria-label="Destaques Daneluci Performance"
    >
      <div className="container mx-auto">
        <div className="relative w-full aspect-[2/3] md:aspect-video overflow-hidden rounded-[32px] bg-neutral-black">
          <div className="sr-only" aria-live="polite">
            Slide {current + 1} de {slides.length}: {slides[current].title}
          </div>
          
          <AnimatePresence mode="wait">
            <m.div
              key={current}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0 size-full"
            >
              <img
                src={getImageUrl(slides[current].image, 1920)}
                srcSet={`
                  ${getImageUrl(slides[current].image, 600)} 600w,
                  ${getImageUrl(slides[current].image, 1200)} 1200w,
                  ${getImageUrl(slides[current].image, 1920)} 1920w,
                  ${getImageUrl(slides[current].image, 2560)} 2560w
                `}
                sizes="100vw"
                alt=""
                className="size-full object-cover opacity-70"
                {...({ fetchpriority: current === 0 ? "high" : "auto" } as any)}
                loading={current === 0 ? "eager" : "lazy"}
                decoding="async"
              />
              
              {/* Overlay de gradiente para legibilidade do texto à esquerda */}
              <div className="absolute inset-0 bg-gradient-to-r from-neutral-black/80 via-neutral-black/20 to-transparent" />

              <div className="absolute inset-0 flex flex-col items-start justify-center px-6 md:px-20 text-left text-white">
                <m.h1 
                  initial={hasInteracted ? { x: -30, opacity: 0 } : { opacity: 1 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="mb-4 text-3xl font-black uppercase italic leading-[0.9] md:text-7xl lg:text-8xl max-w-4xl"
                >
                  {slides[current].title}
                </m.h1>
                <m.p 
                  initial={hasInteracted ? { x: -30, opacity: 0 } : { opacity: 1 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="mb-8 max-w-xl text-base font-medium text-white/70 md:text-xl hidden md:block"
                >
                  {slides[current].description}
                </m.p>
                <m.div
                  initial={hasInteracted ? { x: -30, opacity: 0 } : { opacity: 1 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                >
                  <Link to={slides[current].link}>
                    <Button 
                      title={slides[current].buttonText} 
                      className="bg-brand-cyan px-6 py-4 md:px-8 md:py-6 text-black hover:bg-white transition-all rounded-xl font-black uppercase italic tracking-widest text-sm md:text-base"
                    >
                      {slides[current].buttonText}
                    </Button>
                  </Link>
                </m.div>
              </div>
            </m.div>
          </AnimatePresence>

          {/* Paginação Estilo Wireframe com Setas - Reposicionada para a esquerda */}
          <div 
            className="absolute left-8 md:left-20 z-20 flex items-center gap-6"
            style={{ bottom: '40px', top: 'auto' }}
          >
            {/* Seta Esquerda */}
            <button 
              type="button"
              onClick={prevSlide}
              className="flex size-10 cursor-pointer items-center justify-center rounded-full text-white transition-all hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-brand-cyan"
              aria-label="Slide anterior"
            >
              <FiChevronLeft className="size-6" />
            </button>

            {/* Dots/Pills */}
            <div className="flex items-center gap-3" role="tablist" aria-label="Slides">
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  role="tab"
                  aria-selected={index === current}
                  aria-label={`Ir para o slide ${index + 1}`}
                  onClick={() => goToSlide(index)}
                  className="relative flex size-12 cursor-pointer items-center justify-center focus:outline-none"
                >
                  {index === current ? (
                    <div 
                      className="overflow-hidden rounded-full"
                      style={{ width: '48px', height: '6px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                    >
                      <m.div 
                        key={current}
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ duration: SLIDE_DURATION / 1000, ease: "linear" }}
                        className="h-full"
                        style={{ backgroundColor: 'white' }}
                      />
                    </div>
                  ) : (
                    <div 
                      className="rounded-full transition-all hover:opacity-100" 
                      style={{ 
                        width: '6px', 
                        height: '6px', 
                        backgroundColor: 'white', 
                        opacity: 0.5 
                      }} 
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Seta Direita */}
            <button 
              type="button"
              onClick={nextSlide}
              className="flex size-10 cursor-pointer items-center justify-center rounded-full text-white transition-all hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-brand-cyan"
              aria-label="Próximo slide"
            >
              <FiChevronRight className="size-6" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Preload das próximas imagens locais */}
      {hasInteracted && (
        <div className="hidden" aria-hidden="true">
          {slides.map((slide) => (
            <img key={slide.id} src={slide.image} alt="" loading="lazy" />
          ))}
        </div>
      )}
    </section>
  );
}
